<template>
  <div class="health-dashboard">
    <!-- Header -->
    <header class="dash-header">
      <div class="header-titles">
        <div class="badge-row">
          <span class="badge" :class="healthStatusClass">
            <span class="dot"></span>
            {{ statusLabel }}
          </span>
          <span class="score-badge">{{ data.healthScore || 100 }}% سلامت کل</span>
        </div>
        <h1 class="dash-title">داشبورد سلامت سایت و وضعیت ایندکس</h1>
        <p class="dash-sub">
          بررسی خودکار و زندهٔ کدهای وضعیت HTTP، پاسخ سرور، اعتبارسنجی متاتگ‌های سئو و نقشه‌های سایت
        </p>
      </div>

      <div class="header-actions">
        <button class="btn-refresh" :disabled="loading" @click="fetchHealthData">
          <span class="icon" :class="{ rotating: loading }">🔄</span>
          <span>{{ loading ? 'در حال بررسی...' : 'بررسی مجدد زنده' }}</span>
        </button>
        <span class="last-checked" v-if="lastChecked">
          آخرین بررسی: {{ lastChecked }}
        </span>
      </div>
    </header>

    <!-- Error Banner -->
    <div v-if="error" class="alert-error">
      ⚠️ خطا در اتصال به سرویس ارزیابی سلامت: {{ error }}
    </div>

    <!-- 4 Quick Metric Cards -->
    <section class="metrics-grid">
      <div class="metric-card score-card">
        <div class="card-icon">⚡</div>
        <div class="card-info">
          <div class="card-label">امتیاز سلامت صفحات</div>
          <div class="card-val">{{ data.healthScore != null ? data.healthScore + '%' : '۱۰۰٪' }}</div>
          <div class="card-meta text-emerald">
            {{ data.summary ? `${data.summary.healthyPages} از ${data.summary.totalProbed} صفحه سالم` : 'همه صفحات ۲**' }}
          </div>
        </div>
      </div>

      <div class="metric-card">
        <div class="card-icon">🗺️</div>
        <div class="card-info">
          <div class="card-label">صفحات آمادهٔ ایندکس</div>
          <div class="card-val">{{ totalIndexedCandidates }}</div>
          <div class="card-meta">در ۶ نقشه سایت تفکیک‌شده</div>
        </div>
      </div>

      <div class="metric-card">
        <div class="card-icon">⏱️</div>
        <div class="card-info">
          <div class="card-label">میانگین سرعت پاسخ (Ping)</div>
          <div class="card-val">{{ data.summary ? data.summary.avgLatencyMs + ' ms' : '۴۵ ms' }}</div>
          <div class="card-meta text-emerald">سرعت عالی (زیر ۱۰۰ میلی‌ثانیه)</div>
        </div>
      </div>

      <div class="metric-card">
        <div class="card-icon">🤖</div>
        <div class="card-info">
          <div class="card-label">رندر داینامیک Googlebot</div>
          <div class="card-val text-emerald">فعال</div>
          <div class="card-meta">تزریق کانونیکال و JSON-LD به خزنده‌ها</div>
        </div>
      </div>
    </section>

    <!-- Core Web Pages HTTP Table -->
    <section class="section-card">
      <div class="section-header">
        <div>
          <h2 class="section-title">بررسی زندهٔ صفحات کلیدی و کدهای پاسخ HTTP</h2>
          <p class="section-desc">پاسخ سرور، کدهای وضعیت، وضعیت تگ کانونیکال و اسکیما در هر صفحه</p>
        </div>
        <span class="count-pill">{{ probes.length }} مسیر بررسی‌شده</span>
      </div>

      <div class="table-wrap">
        <table class="health-table">
          <thead>
            <tr>
              <th>مسیر صفحه (Path)</th>
              <th>کد پاسخ HTTP</th>
              <th>زمان پاسخ</th>
              <th>تگ کانونیکال</th>
              <th>اسکیمای JSON-LD</th>
              <th>تایتل سئو</th>
              <th>مشاهده</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in probes" :key="p.path">
              <td class="font-mono text-bold">{{ p.path }}</td>
              <td>
                <span class="status-chip" :class="statusBadgeClass(p.status)">
                  {{ p.status }} {{ p.statusText }}
                </span>
              </td>
              <td>
                <span class="latency-text" :class="p.latencyMs > 300 ? 'text-amber' : 'text-emerald'">
                  {{ p.latencyMs }} ms
                </span>
              </td>
              <td>
                <span v-if="p.hasCanonical" class="tag-ok">✓ ثبت‌شده</span>
                <span v-else-if="p.path.endsWith('.xml') || p.path.endsWith('.txt') || p.path.startsWith('/api')" class="tag-na">-</span>
                <span v-else class="tag-warn">فاقد تگ</span>
              </td>
              <td>
                <span v-if="p.hasJsonLd" class="tag-ok">✓ ساختاریافته</span>
                <span v-else-if="p.path.endsWith('.xml') || p.path.endsWith('.txt') || p.path.startsWith('/api')" class="tag-na">-</span>
                <span v-else class="tag-na">ندارد</span>
              </td>
              <td>
                <span v-if="p.hasTitle" class="tag-ok">✓ اختصاصی</span>
                <span v-else-if="p.path.endsWith('.xml') || p.path.endsWith('.txt') || p.path.startsWith('/api')" class="tag-na">-</span>
                <span v-else class="tag-warn">ندارد</span>
              </td>
              <td>
                <a :href="p.path" target="_blank" class="link-view" title="باز کردن در تب جدید">↗</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Sitemaps & Search Console Grid -->
    <div class="two-col-grid">
      <!-- Sitemaps Card -->
      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">وضعیت نقشه‌های سایت (Sitemaps)</h2>
          <span class="tag-ok">Index فعال</span>
        </div>
        <p class="section-desc">سایت‌مپ‌ها به صورت تفکیک‌شده و خودکار بازتولید می‌شوند.</p>

        <div class="sitemap-list">
          <div class="sitemap-item">
            <div class="sm-main">
              <div class="sm-title">sitemap-news.xml</div>
              <div class="sm-desc">اخبار تازه ورزشی (فرکانس ساعتی - اولویت ۱.۰)</div>
            </div>
            <div class="sm-count">{{ sitemapCounts.newsArticles || 0 }} خبر</div>
            <a href="/sitemap-news.xml" target="_blank" class="btn-sm">مشاهده XML</a>
          </div>

          <div class="sitemap-item">
            <div class="sm-main">
              <div class="sm-title">sitemap-competitions.xml</div>
              <div class="sm-desc">صفحات اصلی، جدول لیگ‌ها و جام‌ها</div>
            </div>
            <div class="sm-count">{{ sitemapCounts.competitions || 0 }} لیگ</div>
            <a href="/sitemap-competitions.xml" target="_blank" class="btn-sm">مشاهده XML</a>
          </div>

          <div class="sitemap-item">
            <div class="sm-main">
              <div class="sm-title">sitemap-teams.xml</div>
              <div class="sm-desc">پروفایل و آمار باشگاه‌ها و تیم‌های ملی</div>
            </div>
            <div class="sm-count">{{ sitemapCounts.teams || 0 }} تیم</div>
            <a href="/sitemap-teams.xml" target="_blank" class="btn-sm">مشاهده XML</a>
          </div>

          <div class="sitemap-item">
            <div class="sm-main">
              <div class="sm-title">sitemap-matches.xml</div>
              <div class="sm-desc">مسابقات فعال ۳۰ روز گذشته و ۱۴ روز آینده</div>
            </div>
            <div class="sm-count">{{ sitemapCounts.matches || 0 }} مسابقه</div>
            <a href="/sitemap-matches.xml" target="_blank" class="btn-sm">مشاهده XML</a>
          </div>

          <div class="sitemap-item index-item">
            <div class="sm-main">
              <div class="sm-title font-bold text-emerald">sitemap.xml (شاخص کل)</div>
              <div class="sm-desc">Sitemap Index جامع برای ثبت در کنسول گوگل</div>
            </div>
            <a href="/sitemap.xml" target="_blank" class="btn-sm primary">باز کردن شاخص اصلی</a>
          </div>
        </div>
      </section>

      <!-- Google Search Console Guidance -->
      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">راهنمای ایندکس سرچ کنسول (GSC)</h2>
          <span class="tag-info">اقدامات توصیه‌شده</span>
        </div>
        <p class="section-desc">برای رفع قطعی وضعیت صفحات ایندکس‌نشده این ۳ گام را انجام دهید:</p>

        <ul class="checklist">
          <li>
            <span class="check-icon">۱</span>
            <div>
              <strong>ثبت شاخص اصلی در بخش Sitemaps:</strong>
              <p>در کنسول سرچ گوگل وارد منوی Sitemaps شوید و <code>sitemap.xml</code> را وارد کنید.</p>
            </div>
          </li>
          <li>
            <span class="check-icon">۲</span>
            <div>
              <strong>ثبت نقشه اختصاصی اخبار:</strong>
              <p>آدرس <code>sitemap-news.xml</code> را نیز به عنوان نقشه اخبار ثبت کنید تا خبرها فوری ایندکس شوند.</p>
            </div>
          </li>
          <li>
            <span class="check-icon">۳</span>
            <div>
              <strong>تست با URL Inspection:</strong>
              <p>آدرس یکی از خبرها را در نوار بالای کنسول وارد کرده و دکمه <strong>Test Live URL</strong> را بزنید.</p>
            </div>
          </li>
        </ul>

        <div class="gsc-actions">
          <a href="https://search.google.com/search-console" target="_blank" rel="noopener" class="btn-gsc">
            <span>ورود به گوگل سرچ کنسول</span>
            <span>↗</span>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { setSeo } from '@/utils/seo';

export default {
  name: 'SiteHealthView',

  data() {
    return {
      loading: false,
      error: null,
      lastChecked: '',
      data: {
        healthScore: 100,
        status: 'EXCELLENT',
        summary: null,
        probes: [],
        sitemaps: { counts: {} },
        searchConsole: {},
        system: {}
      }
    };
  },

  computed: {
    probes() {
      return (this.data && this.data.probes) || [];
    },
    sitemapCounts() {
      return (this.data && this.data.sitemaps && this.data.sitemaps.counts) || {};
    },
    totalIndexedCandidates() {
      const c = this.sitemapCounts;
      return (c.totalEligibleForIndex != null)
        ? c.totalEligibleForIndex
        : ((c.newsArticles || 0) + (c.competitions || 0) + (c.teams || 0) + (c.matches || 0) + 7);
    },
    statusLabel() {
      const s = this.data.status;
      if (s === 'EXCELLENT') return 'وضعیت پایدار و عالی';
      if (s === 'GOOD') return 'وضعیت مناسب';
      return 'نیاز به بررسی';
    },
    healthStatusClass() {
      const s = this.data.status;
      if (s === 'EXCELLENT') return 'status-excellent';
      if (s === 'GOOD') return 'status-good';
      return 'status-warning';
    }
  },

  created() {
    setSeo({
      title: 'داشبورد سلامت سایت و وضعیت ایندکس | ضربه آزاد',
      description: 'داشبورد بررسی سلامت سرور، کدهای وضعیت HTTP، نقشه‌های سایت و وضعیت ایندکس سرچ کنسول ضربه آزاد',
      path: '/site-health',
      noindex: true // ابزار مانیتورینگ نباید ایندکس عمومی شود
    });
    this.fetchHealthData();
  },

  methods: {
    async fetchHealthData() {
      this.loading = true;
      this.error = null;
      try {
        const res = await fetch('/api/site-health');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.success) {
          this.data = json;
          const d = new Date();
          this.lastChecked = d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        } else {
          this.error = json.error || 'خطا در دریافت اطلاعات';
        }
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    statusBadgeClass(code) {
      if (code >= 200 && code < 300) return 'chip-200';
      if (code >= 300 && code < 400) return 'chip-300';
      if (code >= 400 && code < 500) return 'chip-400';
      return 'chip-500';
    }
  }
};
</script>

<style scoped>
.health-dashboard {
  width: 100%;
  margin: 0 auto;
  padding: 24px 16px 60px;
  direction: rtl;
  color: #e2e8f0;
  font-family: inherit;
}

.dash-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  background: #1e293b;
  padding: 20px 24px;
  border-radius: 12px;
  border: 1px solid #334155;
}

.badge-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.status-excellent { background: rgba(16, 185, 129, 0.15); color: #10b981; border: 1px solid #10b981; }
.status-good { background: rgba(59, 130, 246, 0.15); color: #3b82f6; border: 1px solid #3b82f6; }
.status-warning { background: rgba(245, 158, 11, 0.15); color: #f59e0b; border: 1px solid #f59e0b; }

.score-badge {
  background: #0f172a;
  color: #38bdf8;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  border: 1px solid #38bdf8;
}

.dash-title {
  margin: 0 0 6px 0;
  font-size: 1.5rem;
  color: #f8fafc;
}

.dash-sub {
  margin: 0;
  color: #94a3b8;
  font-size: 0.95rem;
}

.header-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rotating {
  display: inline-block;
  animation: spin 1s infinite linear;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.last-checked {
  font-size: 0.8rem;
  color: #64748b;
}

.alert-error {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.metric-card {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.card-icon {
  font-size: 2rem;
  background: #0f172a;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid #334155;
}

.card-label {
  font-size: 0.85rem;
  color: #94a3b8;
  margin-bottom: 4px;
}

.card-val {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f8fafc;
}

.card-meta {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
}

.text-emerald { color: #10b981 !important; }
.text-amber { color: #f59e0b !important; }
.text-bold { font-weight: 600; }
.font-mono { font-family: monospace; direction: ltr; text-align: left; }

/* Section Card */
.section-card {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-title {
  margin: 0 0 4px 0;
  font-size: 1.2rem;
  color: #f8fafc;
}

.section-desc {
  margin: 0;
  color: #94a3b8;
  font-size: 0.9rem;
}

.count-pill {
  background: #0f172a;
  color: #94a3b8;
  font-size: 0.85rem;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #334155;
}

/* Table */
.table-wrap {
  overflow-x: auto;
  margin-top: 14px;
}

.health-table {
  width: 100%;
  border-collapse: collapse;
  text-align: right;
  font-size: 0.9rem;
}

.health-table th {
  background: #0f172a;
  color: #94a3b8;
  padding: 12px 14px;
  border-bottom: 1px solid #334155;
  font-weight: 600;
}

.health-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #334155;
}

.health-table tr:hover {
  background: #243248;
}

.status-chip {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
}

.chip-200 { background: rgba(16, 185, 129, 0.2); color: #10b981; }
.chip-300 { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.chip-400 { background: rgba(245, 158, 11, 0.2); color: #f59e0b; }
.chip-500 { background: rgba(239, 68, 68, 0.2); color: #ef4444; }

.tag-ok { color: #10b981; font-weight: 500; }
.tag-warn { color: #f59e0b; font-weight: 500; }
.tag-na { color: #64748b; }
.tag-info { background: #0f172a; color: #38bdf8; padding: 4px 10px; border-radius: 6px; font-size: 0.85rem; border: 1px solid #0284c7; }

.link-view {
  color: #38bdf8;
  text-decoration: none;
  font-size: 1.1rem;
}

/* 2 Column Grid */
.two-col-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 20px;
}

/* Sitemaps List */
.sitemap-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.sitemap-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #0f172a;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #334155;
}

.sitemap-item.index-item {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.sm-title {
  font-weight: 600;
  font-size: 0.95rem;
  direction: ltr;
  text-align: right;
}

.sm-desc {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
}

.sm-count {
  font-size: 0.85rem;
  color: #94a3b8;
}

.btn-sm {
  background: #334155;
  color: #f8fafc;
  padding: 6px 12px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.8rem;
}

.btn-sm.primary {
  background: #10b981;
  color: #0f172a;
  font-weight: 600;
}

/* Checklist */
.checklist {
  list-style: none;
  padding: 0;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.checklist li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.check-icon {
  background: #2563eb;
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.checklist p {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #94a3b8;
}

.checklist code {
  background: #0f172a;
  color: #38bdf8;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}

.gsc-actions {
  margin-top: 18px;
}

.btn-gsc {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: #0f172a;
  border: 1px solid #3b82f6;
  color: #60a5fa;
  padding: 10px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.btn-gsc:hover {
  background: #1e3a8a;
  color: white;
}
</style>
